import { Navigate, useLocation } from "react-router-dom";
import { useResearchSession } from "@/hooks/useResearchSession";
import { canManageProjects, canManageResearchUsers, canReview } from "@/lib/research/access";

export type ResearchRequirement = "access" | "review" | "manage" | "admin";

/**
 * Route guard for Research Operations.
 *
 * Frontend gating is a convenience only — every research table and function is
 * protected server-side by row-level security and SECURITY DEFINER checks.
 */
export function ResearchGuard({
  children,
  require: requirement = "access",
}: {
  children: React.ReactNode;
  require?: ResearchRequirement;
}) {
  const { session, isLoading } = useResearchSession();
  const location = useLocation();

  if (isLoading) return <div className="min-h-screen-safe bg-background" />;

  if (!session.authenticated) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/research/login?next=${next}`} replace />;
  }

  const permitted =
    requirement === "admin"
      ? canManageResearchUsers(session)
      : requirement === "manage"
        ? canManageProjects(session)
        : requirement === "review"
          ? canReview(session)
          : session.hasAccess;

  if (!permitted) {
    const reason = session.status === "suspended" ? "suspended" : session.status === "expired" ? "expired" : "no-access";
    return <Navigate to={`/research/access-denied?reason=${reason}`} replace />;
  }

  return <>{children}</>;
}

export default ResearchGuard;
